import { useState, useEffect } from 'react';
import type { DialerContextValue } from '@/contexts/DialerContextTypes';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { listCustomers, updateCustomer } from '@/api/endpoints/customers';
import { getTenantCoaching, updateTenantCoaching } from '@/api/endpoints/tenant';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';

// ─── Live coach prompts (W4: shown in Paramètres > IA) ──────────────────────
//
// Three layers, merged by the server for every live coaching request: the
// base prompt (read-only, the server's), the organisation's prompt, and the
// selected client's. The last two are written by a tenant admin only
// (PATCH /api/tenant/coaching and PATCH /api/customers/:id refuse anyone
// else); a caller reads them, without a Save button that would fail.
// Each editor says when its text differs from what is saved (« Modifié »),
// and the client's card names the client it applies to.

export function CoachBasePromptCard() {
  const { t } = useTranslation(['settings', 'common']);
  const { data, isLoading } = useQuery({
    queryKey: ['coach-base-prompt'],
    queryFn: () => api.functions.invoke('liveCoachBasePrompt'),
    staleTime: 10 * 60 * 1000, // rarement change, 10min
  });
  // api.functions.invoke returns { data, status } — le vrai payload est data.data
  const prompt = (data as { data?: { prompt?: string } } | undefined)?.data?.prompt || '';

  return (
    <div className="rounded-xl border border-border bg-muted/20 px-5 py-4 space-y-2">
      <div>
        <p className="text-sm font-semibold text-foreground">{t('settings:coaching.basePromptTitle')}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{t('settings:coaching.basePromptHint')}</p>
      </div>
      <pre
        className="text-xs font-mono leading-relaxed whitespace-pre-wrap bg-background border border-border rounded-lg px-3 py-2 text-muted-foreground select-text"
        style={{ maxHeight: 200, overflowY: 'auto' }}
      >
        {isLoading ? t('common:actions.loading') : prompt || t('settings:coaching.noBasePrompt')}
      </pre>
    </div>
  );
}

/** One coach prompt: its title, its text, « Modifié » and Save for an admin, read-only otherwise. */
function CoachPromptEditor({
  testId, title, subtitle, saved, placeholder, canEdit, onSave,
}: {
  testId: string;
  title: string;
  subtitle?: string;
  saved: string;
  placeholder: string;
  canEdit: boolean;
  onSave: (value: string) => Promise<void>;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(saved); }, [saved]);
  const dirty = value !== saved;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(value);
      toast.success(t('settings:coaching.saved'));
    } catch (e) {
      toast.error(errorMessage(e, t, 'settings:coaching.saveErrorDefault'), errorToastOptions(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card px-5 py-5 space-y-3" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {canEdit && dirty && (
          <span className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded" style={{ background: 'var(--vf-yellow-bg)', color: 'var(--vf-yellow)' }} data-testid={`${testId}-dirty`}>
            {t('settings:coaching.modified')}
          </span>
        )}
      </div>
      <textarea
        value={value}
        readOnly={!canEdit}
        aria-label={title}
        onChange={(e) => setValue(e.target.value)}
        placeholder={canEdit ? placeholder : t('settings:coaching.emptyReadOnly')}
        className={`w-full min-h-[240px] px-4 py-3 text-sm border border-border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[var(--vf-accent)] font-mono leading-relaxed ${canEdit ? 'bg-background' : 'bg-muted/30 text-muted-foreground'}`}
        rows={12}
      />
      {canEdit ? (
        <div className="flex justify-end gap-2">
          {dirty && (
            <Button variant="ghost" onClick={() => setValue(saved)} disabled={saving}>
              {t('settings:coaching.discard')}
            </Button>
          )}
          <Button onClick={() => void save()} disabled={saving || !dirty}>
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-readonly`}>{t('settings:coaching.readOnly')}</p>
      )}
    </div>
  );
}

// ─── The organisation's prompt (every client) ─────────────────────────────

export function TenantCoachingSection() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['settings', 'common']);
  const { isTenantAdmin } = usePermissions();

  // ADR-031 — tenant-level coaching prompt: applies to every Customer.
  // CoachingResolver merges this with the per-Customer override at runtime.
  const { data: coaching } = useQuery({
    queryKey: ['tenant-coaching'],
    queryFn: ({ signal }) => getTenantCoaching({ signal }),
    staleTime: 60_000,
  });

  return (
    <CoachPromptEditor
      testId="coach-tenant"
      title={t('settings:coaching.systemPromptLabel')}
      subtitle={t('settings:coaching.tenantSubtitle')}
      saved={coaching?.coaching_system_prompt || ''}
      placeholder={t('settings:coaching.systemPromptPlaceholder')}
      canEdit={isTenantAdmin}
      onSave={async (value) => {
        await updateTenantCoaching({ coaching_system_prompt: value });
        await queryClient.invalidateQueries({ queryKey: ['tenant-coaching'] });
      }}
    />
  );
}

// ─── The selected client's prompt (added to the organisation's) ───────────

export function CustomerCoachingSection({ d }: { d: DialerContextValue }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['settings', 'common']);
  const { isTenantAdmin } = usePermissions();

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    enabled: !!d.callerData,
    staleTime: 60_000,
  });

  const currentCustomer = customers.find((c) => c.id === d.selectedCustomerId);

  if (!currentCustomer) {
    return (
      <div className="rounded-xl border border-border bg-muted/30 px-5 py-4 text-sm text-muted-foreground">
        {t('settings:coaching.selectCustomer')}
      </div>
    );
  }

  return (
    <CoachPromptEditor
      testId="coach-customer"
      title={t('settings:coaching.customerTitle', { name: currentCustomer.company_name })}
      subtitle={t('settings:coaching.customerSubtitle')}
      saved={currentCustomer.coaching_system_prompt || ''}
      placeholder={t('settings:coaching.customerPlaceholder', { name: currentCustomer.company_name })}
      canEdit={isTenantAdmin}
      onSave={async (value) => {
        await updateCustomer(currentCustomer.id, { coaching_system_prompt: value });
        await queryClient.invalidateQueries({ queryKey: ['customers'] });
      }}
    />
  );
}
